import React from 'react'
import styled, { css } from 'styled-components'
import SidebarHeader from './SidebarHeader'
import SidebarSection from './SidebarSection'
import SidebarFooter from './SidebarFooter'
import SidebarVersion from './SidebarVersion'
import NavMenu from './NavMenu/NavMenu'
import type { ModuleEntry } from './NavMenu/NavItem'

export type SidebarMode = 'desktop' | 'mobile-drawer'

interface UserInfo {
  name?: string
  subRole?: string
  companyName?: string
}

interface SidebarProps {
  mode: SidebarMode
  collapsed?: boolean
  onToggleCollapse?: () => void
  drawerOpen?: boolean
  onCloseDrawer?: () => void
  modules: ModuleEntry[]
  pathname: string
  showTestLink?: boolean
  showSettings?: boolean
  user: UserInfo
  appVersion?: string
  hasNewVersion?: boolean
  onOpenPatchNote?: () => void
  onOpenVersionPrompt?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({
  mode,
  collapsed = false,
  onToggleCollapse,
  drawerOpen = false,
  onCloseDrawer,
  modules,
  pathname,
  showTestLink,
  showSettings,
  user,
  appVersion,
  hasNewVersion,
  onOpenPatchNote,
  onOpenVersionPrompt,
}) => {
  const isMobile = mode === 'mobile-drawer'
  const handleItemClick = isMobile ? onCloseDrawer : undefined

  return (
    <>
      {isMobile && <Scrim $open={drawerOpen} onClick={onCloseDrawer} aria-hidden />}
      <Frame
        $mode={mode}
        $collapsed={collapsed}
        $open={drawerOpen}
        role={isMobile ? 'dialog' : undefined}
        aria-modal={isMobile ? drawerOpen : undefined}
      >
        <SidebarHeader
          mode={mode}
          collapsed={collapsed}
          brandName={user.companyName || 'אגרוליין'}
          brandSub={user.name}
          onToggleCollapse={onToggleCollapse}
          onCloseDrawer={onCloseDrawer}
        />
        <Body>
          <SidebarSection label='ראשי' collapsed={collapsed}>
            <NavMenu
              modules={modules}
              pathname={pathname}
              collapsed={collapsed}
              showTestLink={showTestLink}
              onCloseMenu={handleItemClick}
              onExpand={mode === 'desktop' && collapsed ? onToggleCollapse : undefined}
            />
          </SidebarSection>
        </Body>
        <SidebarVersion
          collapsed={collapsed}
          version={appVersion}
          hasNew={hasNewVersion}
          onVersionClick={onOpenPatchNote}
          onNewClick={onOpenVersionPrompt}
        />
        <SidebarFooter
          collapsed={collapsed}
          showSettings={showSettings}
          onItemClick={handleItemClick}
        />
      </Frame>
    </>
  )
}

export default Sidebar

const SIDEBAR_W = '264px'
const SIDEBAR_W_MINI = '72px'
const DRAWER_W = '280px'

const Scrim = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.32);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease;
  z-index: 60;
`

const Frame = styled.aside<{
  $mode: SidebarMode
  $collapsed: boolean
  $open: boolean
}>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02), 0 12px 32px -20px rgba(0, 0, 0, 0.08);
  overflow: visible;
  direction: rtl;

  ${({ $mode, $collapsed }) =>
    $mode === 'desktop' &&
    css`
      position: sticky;
      top: 12px;
      align-self: flex-start;
      height: calc(100vh - 24px);
      width: ${$collapsed ? SIDEBAR_W_MINI : SIDEBAR_W};
      flex-shrink: 0;
      transition: width 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
    `}

  ${({ $mode, $open }) =>
    $mode === 'mobile-drawer' &&
    css`
      position: fixed;
      top: 12px;
      bottom: 12px;
      inset-inline-start: 12px;
      width: ${DRAWER_W};
      transform: translateX(${$open ? '0' : 'calc(100% + 24px)'});
      transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
      z-index: 70;
    `}
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 14px 14px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`
